<script setup>
import {defineProps, computed, watch, defineEmits, onMounted, ref, nextTick} from 'vue'
import Jumbo from '@/components/Docs/Jumbo.vue'
import DocSection from '@/components/Docs/Section.vue'
import CodeBlock from '@/components/Docs/CodeBlock.vue'
import JsonBlock from '@/components/Docs/JsonBlock.vue'


import StringParam from '@/components/Docs/Parameters/StringParam.vue'
import EndpointBlock from '@/components/Docs/EndpointBlock.vue'


import BasicAttribute from '@/components/Docs/Attributes/BasicAttribute.vue'
import { provideDocExpansion } from '@/composables/useDocExpansion'

let request_ip = ref("89.36.76.126")

let get_query = computed(() => {
  return "/query/ip/" + request_ip.value
})

let dynamic_title = computed(() => {
  return "/query/ip/" + request_ip.value
})

// Master expand/collapse control
const { triggerExpandAll } = provideDocExpansion()
const allExpanded = ref(false)

const toggleAllExpansion = () => {
  allExpanded.value = !allExpanded.value
  triggerExpandAll(allExpanded.value)
}

</script>

<template>
  <div>
    <doc-section title="IP information">
      <template v-slot:left>
        <div class="mb-5">
        This endpoint returns a collection of general information on an IP address. <br><br>
        Note that the returned information may vary depending on the plan that you are currently on. For instance provider and operator information requires the Basic plan, and behavioral information require the Professional plan.
        </div>
      
        <div class="mb-5">
          <h4 class="mb-4">Request parameters</h4>

          <StringParam class="mb-3" name="IP address" :required="true" description="The ip address you wish to know about" type="string" :value="request_ip" @value="(v) => request_ip = v" />
        </div>

        <div>
          <div class="d-flex justify-content-between align-items-center mb-4">
            <h4 class="mb-0">Response attributes</h4>
            <button @click="toggleAllExpansion" class="btn-expand-collapse">
              <font-awesome-icon :icon="['fas', allExpanded ? 'compress-alt' : 'expand-alt']" />
              {{ allExpanded ? 'Collapse All' : 'Expand All' }}
            </button>
          </div>

          <BasicAttribute name="status" type="string" description="The status of the request's response" />

          <BasicAttribute name="data" type="object" description="The requested information">

            <BasicAttribute name="ip" type="string" description="The target IP address" />

            <BasicAttribute name="location" type="object" description="The location of the ip" :depth="1">
              <BasicAttribute name="country_iso" type="string" description="Two letter country code" />
              <BasicAttribute name="city" type="string" description="The full name of the city" />
            </BasicAttribute>

            <BasicAttribute name="anonymization" type="object" description="" :depth="1">
              <BasicAttribute name="vpn" type="boolean" description="whether or not the ip has been used as a vpn" />
              <BasicAttribute name="proxy" type="boolean" description="whether or not the ip has been used as a proxy" />
              <BasicAttribute name="tor" type="boolean" description="whether or not the ip is involved in the tor network" />
              <BasicAttribute name="services" type="array" description="List of anonymization service providers associated with this IP" :depth="2">
                <BasicAttribute name="provider" type="string" description="The canonical provider identifier" />
                <BasicAttribute name="display_name" type="string" description="Pretty display name of the provider" />
                <BasicAttribute name="thumbnail_url" type="string" description="URL to provider logo/icon" />
              </BasicAttribute>
            </BasicAttribute>

            <BasicAttribute name="network" type="object" description="" :depth="1">
              <BasicAttribute name="type" type="string" description="The likely type of infrastructure used to operate the ip" />
              <BasicAttribute name="service" type="string" description="The service the ip is a part of. For instance S3, cdn.." />
              <BasicAttribute name="region" type="string" description="This will usually map to a cloud providers regional mappings" />
              <BasicAttribute name="range" type="string" description="The closest allocation of addresses associated with the ip address" />
              <BasicAttribute name="private" type="boolean" description="whether or not the ip belongs to a reversed network space" />
            </BasicAttribute>

            <BasicAttribute name="as" type="object" description="" :depth="1">
              <BasicAttribute name="as_number" type="integer" description="The AS number of the organization owning the ip address allocation (nullable)" />
              <BasicAttribute name="organization" type="string" description="The name of the organization owning the ip address allocation" />
            </BasicAttribute>

            <BasicAttribute name="hostnames" type="string array" description="List of hostnames announced by the IP or operator" />

            <BasicAttribute name="company" type="object" description="" :depth="1">
              <BasicAttribute name="name" type="string" description="The entity managing the ip address" />
              <BasicAttribute name="business" type="string array" description="A list of services the company provides as part of its business" />
              <BasicAttribute name="description" type="string" description="A brief description of who the network provider is" />
            </BasicAttribute>

            <BasicAttribute name="osint" type="object" description="" :depth="1">
              <BasicAttribute name="services" type="array" description="List of OSINT providers that have reported on this IP" :depth="2">
                <BasicAttribute name="provider" type="string" description="The canonical provider identifier" />
                <BasicAttribute name="display_name" type="string" description="Pretty display name of the provider" />
                <BasicAttribute name="thumbnail_url" type="string" description="URL to provider logo/icon" />
                <BasicAttribute name="tags" type="string array" description="Activity tags reported by this provider" />
                <BasicAttribute name="first_seen" type="string" description="When this provider first reported on this IP" />
                <BasicAttribute name="last_seen" type="string" description="When this provider last reported on this IP" />
              </BasicAttribute>
              <BasicAttribute name="first_seen" type="string" description="Earliest reporting time across all providers" />
              <BasicAttribute name="last_seen" type="string" description="Most recent reporting time across all providers" />
            </BasicAttribute>

            <BasicAttribute name="netflow" type="object" description="Network flow telemetry data from passive DNS collection and traffic analysis" :depth="1">
              <BasicAttribute name="has_netflow" type="boolean" description="Indicates whether netflow data is available for this IP address" />
              <BasicAttribute name="days_seen" type="integer" description="Number of unique days this IP has been observed in network flow data" />
              <BasicAttribute name="total_observation" type="integer" description="Total number of netflow observations recorded for this IP address" />
              <BasicAttribute name="last_seen" type="string" description="When we last observed this IP in network flow data" />
            </BasicAttribute>

          </BasicAttribute>


        </div>

      </template>

      <template v-slot:right>
        <endpoint-block
          :title="dynamic_title"
          method="GET"
          name="IP Information"
          :query="get_query"
        />

        <json-block title="Response">
{
"status":
"success",
"data":
{
"ip":
"89.36.76.126",
"location":
{
"country_iso":
"DE",
"city":
"Berlin"
},
"anonymization":
{
"vpn":
true,
"proxy":
false,
"tor":
false,
"services":
[
{
"provider":
"surfshark",
"display_name":
"Surfshark VPN",
"thumbnail_url":
"https://example.com/surfshark-logo.png"
}
]
},
"network":
{
"type":
"Cloud",
"service":
"",
"region":
"",
"range":
"89.36.76.0/24",
"private":
false
},
"as":
{
"organization":
"M247 Europe SRL",
"as_number":
9009
},
"hostnames":
null,
"company":
{
"name":
"M247",
"business":
[
"Backup/Recovery",
"Business Internet",
"Cloud",
"Colocation",
"Hosting",
"VPC"
],
"description":
"An internet service provider offering a range of services including broadband connectivity, data center solutions, and cloud services."
},
"osint":
{
"services":
[
{
"provider":
"abuseipdb",
"display_name":
"AbuseIPDB",
"thumbnail_url":
"https://example.com/abuseipdb-logo.png",
"tags":
[
"malware",
"bruteforce"
],
"first_seen":
"2024-01-15T10:30:00Z",
"last_seen":
"2024-03-20T15:45:00Z"
}
],
"first_seen":
"2024-01-15T10:30:00Z",
"last_seen":
"2024-03-20T15:45:00Z"
},
"netflow":
{
"has_netflow":
true,
"days_seen":
45,
"total_observation":
1234,
"last_seen":
"2025-03-20T13:19:46Z"
}
}
}
        </json-block>
      </template>
    </doc-section>
  </div>
</template>

<style scoped>
.btn-expand-collapse {
  background-color: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #6B7280;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-expand-collapse:hover {
  background-color: rgba(255, 255, 255, 0.05);
  color: #E5E7EB;
}
</style>
